import React, { createContext, useContext, useEffect, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import {
    confirmSignUp as amplifyConfirmSignUp,
    fetchAuthSession,
    signIn as amplifySignIn,
    signInWithRedirect,
    signOut as amplifySignOut,
    signUp as amplifySignUp,
} from 'aws-amplify/auth';
import { claimIdentity, getAccount, provisionAccount } from '../services/account-service';
import { getMyIdentity, setMyIdentity } from '../services/player-identity-service';
import { recentRoundsService } from '../services/recent-rounds-service';
import { HttpError } from '../services/round-service';

interface AuthState {
    userId: string | null;
    playerId: string | null;
    loading: boolean;
    authError: string | null;
    claimMessage: string | null;
}

interface AuthContextValue extends AuthState {
    signUp: (email: string, password: string) => Promise<void>;
    confirmSignUp: (email: string, code: string) => Promise<void>;
    signIn: (email: string, password: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
    clearAuthError: () => void;
    clearClaimMessage: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const clearErrorParamsFromUrl = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('error') && !url.searchParams.has('error_description')) return;
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

// Cognito's OAuth failure redirect (e.g. an IdP attribute-mapping error) always causes a
// full page load. Amplify's own signInWithRedirect_failure Hub event can fire before React
// has mounted and subscribed a listener, since the failure path is pure microtasks with no
// network round trip. Reading the error directly off the URL on the initial render is
// synchronous and timing-independent, so it's the reliable path; the Hub listener below is
// kept only as a defensive fallback for the (unlikely) case of an in-session failure.
const readErrorFromUrl = (): string | null => {
    const url = new URL(window.location.href);
    const error = url.searchParams.get('error');
    if (!error) return null;
    return url.searchParams.get('error_description') || error;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<AuthState>(() => ({
        userId: null,
        playerId: null,
        loading: true,
        authError: readErrorFromUrl(),
        claimMessage: null,
    }));

    const completeSignIn = async () => {
        const session = await fetchAuthSession();
        const sub = session.tokens?.idToken?.payload?.sub as string | undefined;
        if (!sub) throw new Error('No user ID in session');

        try {
            const account = await getAccount();
            setMyIdentity({ id: account.id, name: account.name });
            setState({ userId: sub, playerId: account.id, loading: false, authError: null, claimMessage: null });
            return;
        } catch (error) {
            if (!(error instanceof HttpError) || error.status !== 404) throw error;
        }

        // First login for this Cognito user: link the device's existing anonymous player
        // (if any) instead of provisioning a disconnected one, so its recent rounds carry over.
        const localIdentity = getMyIdentity();
        if (localIdentity) {
            try {
                const roundIds = recentRoundsService
                    .getRecentRounds()
                    .filter((r) => r.playerId === localIdentity.id)
                    .map((r) => r.roundId);
                const claimed = await claimIdentity(localIdentity.id, roundIds);
                recentRoundsService.markRoundsPermanent(roundIds);
                setMyIdentity({ id: claimed.id, name: claimed.name });
                setState({
                    userId: sub,
                    playerId: claimed.id,
                    loading: false,
                    authError: null,
                    claimMessage: roundIds.length > 0 ? 'Your recent rounds have been linked to your account.' : null,
                });
                return;
            } catch {
                // Fall through to provision — e.g. lost a claim race, or the local identity was stale.
            }
        }

        const account = await provisionAccount();
        setMyIdentity({ id: account.id, name: account.name });
        setState({ userId: sub, playerId: account.id, loading: false, authError: null, claimMessage: null });
    };

    useEffect(() => {
        clearErrorParamsFromUrl();

        // Restoring an already-logged-in session also provisions/fetches the account, the
        // same way a fresh sign-in does — otherwise a plain page reload leaves playerId
        // null until the next explicit login, which is exactly the "feels like a different
        // device" symptom this is meant to fix. Not logged in (no sub) throws before any
        // network call, so anonymous visitors hit no extra request.
        completeSignIn().catch(() => setState((prev) => ({ ...prev, userId: null, playerId: null, loading: false })));

        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            if (payload.event === 'signInWithRedirect') {
                completeSignIn().catch(() =>
                    setState((prev) => ({ ...prev, userId: null, playerId: null, loading: false }))
                );
            } else if (payload.event === 'signInWithRedirect_failure') {
                const message = payload.data.error?.message ?? 'Google sign-in failed';
                setState((prev) => ({ ...prev, loading: false, authError: message }));
                clearErrorParamsFromUrl();
            }
        });

        return unsubscribe;
    }, []);

    const clearAuthError = () => setState((prev) => ({ ...prev, authError: null }));
    const clearClaimMessage = () => setState((prev) => ({ ...prev, claimMessage: null }));

    const signUp = async (email: string, password: string) => {
        await amplifySignUp({ username: email, password, options: { userAttributes: { email } } });
    };

    const confirmSignUp = async (email: string, code: string) => {
        await amplifyConfirmSignUp({ username: email, confirmationCode: code });
    };

    const signIn = async (email: string, password: string) => {
        await amplifySignIn({ username: email, password });
        await completeSignIn();
    };

    const signInWithGoogle = async () => {
        await signInWithRedirect({ provider: 'Google' });
    };

    const signOut = async () => {
        await amplifySignOut();
        setState({ userId: null, playerId: null, loading: false, authError: null, claimMessage: null });
    };

    return (
        <AuthContext.Provider
            value={{
                ...state,
                signUp,
                confirmSignUp,
                signIn,
                signInWithGoogle,
                signOut,
                clearAuthError,
                clearClaimMessage,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
